import { useEffect, useRef } from 'react';
import { Button, cn } from '@strategy4kids/ui';
import { CardBack } from '../PlayingCard.js';

export type RoundOutcome = 'win' | 'lose' | 'draw' | 'neutral';

/** One game-agnostic round result, built by each game's store from what it already knows. */
export interface RoundSummary {
  readonly round: number;
  readonly outcome: RoundOutcome;
  readonly headline: string;
  readonly detail?: string;
  readonly rows: readonly {
    readonly key: string;
    readonly label: string;
    readonly portrait?: string | null;
    readonly delta: string;
    readonly total: string | number;
    readonly you?: boolean;
    readonly leading?: boolean;
  }[];
  readonly gameOver?: boolean;
}

const TONE: Record<RoundOutcome, string> = {
  win: 'from-emerald-400/40 to-sun/30 ring-emerald-500',
  lose: 'from-rose-300/40 to-slate-300/30 ring-rose-400',
  draw: 'from-sky/30 to-slate-200/30 ring-sky',
  neutral: 'from-accent-soft to-surface ring-accent/40',
};

/** The end-of-round card: who got what this round, the new totals, and the way on. */
export function RoundSummaryPanel({
  testid,
  summary,
  nextLabel,
  onNext,
  nextTestid,
  headlineTestid,
}: {
  readonly testid: string;
  readonly summary: RoundSummary;
  readonly nextLabel: string;
  readonly onNext: () => void;
  readonly nextTestid: string;
  /** Overrides the headline's test id (default `<testid>-headline`) for screens whose tests already know one. */
  readonly headlineTestid?: string;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  // The result appears below a long table on a phone: bring it (and its "next" button) into view, gently when
  // the player prefers reduced motion. The panel's bottom edge lands on the viewport's bottom edge.
  useEffect(() => {
    const el = panelRef.current;
    if (!el || typeof el.scrollIntoView !== 'function') return;
    const calm =
      document.documentElement.classList.contains('reduce-motion') ||
      (typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    el.scrollIntoView({ block: 'end', behavior: calm ? 'auto' : 'smooth' });
  }, []);
  return (
    <div
      ref={panelRef}
      className={cn('flex flex-col gap-4 rounded-3xl bg-gradient-to-br p-5 shadow-xl ring-2 backdrop-blur lg:p-8', TONE[summary.outcome])}
      data-testid={testid}
      data-outcome={summary.outcome}
      data-appdom-id={testid}
      data-appdom-type="Panel"
      aria-live="polite"
    >
      <div>
        <h2 className="font-display text-h2 font-bold text-fg" data-testid={headlineTestid ?? `${testid}-headline`}>
          {summary.headline}
        </h2>
        {summary.detail ? <p className="mt-1 text-base text-fg-muted lg:text-lg">{summary.detail}</p> : null}
      </div>
      <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
        {summary.rows.map((r) => (
          <li
            key={r.key}
            data-testid={`${testid}-${r.key}`}
            className={cn(
              'flex items-center gap-3 rounded-2xl border bg-surface/90 p-3',
              r.you ? 'border-accent' : 'border-border',
              r.leading && 'ring-2 ring-sun',
            )}
          >
            <span className="flex size-12 shrink-0 items-center justify-center overflow-hidden rounded-full bg-accent-soft lg:size-14">
              {r.portrait ? <img src={r.portrait} alt="" aria-hidden="true" className="size-full object-cover" /> : <CardBack className="!w-6" />}
            </span>
            <span className="flex min-w-0 flex-col">
              <span className="truncate text-sm font-bold text-fg-muted lg:text-base">{r.label}</span>
              <span className="flex items-baseline gap-2">
                <span className="font-display text-2xl font-bold text-fg lg:text-3xl">{r.total}</span>
                <span className="text-base font-bold text-accent">{r.delta}</span>
              </span>
            </span>
          </li>
        ))}
      </ul>
      <div className="flex justify-end">
        <Button size="lg" onClick={onNext} data-testid={nextTestid}>
          {nextLabel}
        </Button>
      </div>
    </div>
  );
}
